import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { AlertCircleIcon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleSSOSignInOrSignUp } from './authConfig';
import {
  AuthViewHeader,
  AuthViewTitle,
  AuthViewWrapper,
  AuthWrapper,
  LogoWrapper,
} from './AuthStyles';
import logo from '/ui/new-spellcaster-logo.svg';

export default function AdminLogin() {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ReactNode | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(
        api.asEducator.auth,
        email,
        password,
      );
      const userProfile = await api.asEducator.getUserProfile(user.uid);
      if (userProfile) {
        trackMixpanelEvent('Admin Login via email and password', {
          name: userProfile.name,
          email: userProfile.email,
        });
      } else {
        setError('Email or password is incorrect');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error during email and password sign in:', error);
      setLoading(false);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? error.code
          : 'unknown';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password') {
        setError(
          "Invalid email or password. If you don't have an account, please sign up.",
        );
      } else {
        setError(UNKNOWN_ERROR_MESSAGE);
      }
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setLoading(true);
    try {
      await handleSSOSignInOrSignUp();
      trackMixpanelEvent('User Signed In via SSO');
      setLoading(false);
    } catch (error) {
      console.error('Error during Google sign in:', error);
      setLoading(false);
      setError(UNKNOWN_ERROR_MESSAGE);
    }
  }

  return (
    <AuthViewWrapper>
      <AuthViewHeader>
        <LogoWrapper onClick={() => navigate('/')}>
          <img src={logo} alt="logo" />
        </LogoWrapper>
        <AuthViewTitle>Educator Sign In</AuthViewTitle>

        <span className="text-sm mt-2 mb-4 text-muted-foreground">
          Looking for learner sign in?{' '}
          <b
            className="cursor-pointer hover:underline text-primary font-semibold"
            onClick={() => navigate('/game')}
          >
            Click here
          </b>
        </span>
      </AuthViewHeader>

      <AuthWrapper onSubmit={handleSubmit}>
        {error && (
          <Alert variant="destructive">
            <AlertCircleIcon size={16} className="ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          disabled={isLoading}
        />
        <div className="w-full">
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-foreground"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeSlashIcon className="size-4" />
              ) : (
                <EyeIcon className="size-4" />
              )}
            </button>
          </div>
          <Button
            type="button"
            variant="link"
            disabled={isLoading}
            className="p-0 h-fit text-xs ml-auto block mt-1"
            onClick={() => navigate('/forgot-password')}
          >
            Forgot password?
          </Button>
        </div>
        <Button type="submit" loading={isLoading} className="w-full">
          Sign In
        </Button>
        <Button
          type="button"
          variant="link"
          disabled={isLoading}
          className="p-0 h-fit"
          onClick={() => navigate('/signup')}
        >
          Don't have an account? Sign up
        </Button>

        <div className="flex items-center gap-3 w-full my-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          type="button"
          variant="outline"
          disabled={isLoading}
          className="w-full"
          onClick={handleGoogleSignIn}
        >
          <img src="/icons/google.svg" alt="Google" className="size-4 mr-2" />
          Continue with Google
        </Button>
      </AuthWrapper>
    </AuthViewWrapper>
  );
}
